import React, { useContext, useEffect, useState } from 'react';
import { Button, OverlayTrigger, ProgressBar, Tooltip, Spinner } from 'react-bootstrap';
import Table from '../../../components/Table';
import { UserProfileContext } from '../../../context/userProfileContext';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import DateColumn from '../../../components/DateColumn';
import './timeline.scss';
import Modules from '../../../utils/modules';
import { deletePension } from '../../../utils/api/pension';
import camelCaseColumn from '../../../components/camelCaseColumn';
import { useTranslation } from 'react-i18next';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { formatDate } from '../../../utils/formatDate';
import { deleteInsurance } from '../../../utils/api/insurance';

interface TimeLineProps {
    createNewRoundEnabled?: boolean;
    SecondButton?: React.ReactElement;
    onCreateRound?: (reason: string) => Promise<any>;
}

const TimeLine = ({ createNewRoundEnabled = true, SecondButton, onCreateRound }: TimeLineProps) => {
    const [rounds, setRounds] = useState<any[]>([]);

    const { profileContextData, setProfileContextData, onEditRound, onCreateNewRound, isRoundBeingCreated, users, allUsers } =
        useContext(UserProfileContext);

    const { client, selectedModule } = profileContextData;

    const currentModuleName = profileContextData?.selectedModule?.data?.moduleName;

    const { t } = useTranslation('components');

    useEffect(() => {
        setRounds(selectedModule.data.rounds);
        // console.log(selectedModule.data.rounds[selectedModule.data.rounds.length - 1]);
        console.log(profileContextData);
    }, [profileContextData]);

    const onSelectRound = (round: any) => {
        if (selectedModule?.data?.permittedToView) {
            setProfileContextData((prev: any) => ({
                ...prev,
                currentRound: round,
            }));
        }
    };

    const canCreateNewRound = () => {
        const isRoundNotBeingCreated = !isRoundBeingCreated;
        const isCreateNewRoundEnabled = createNewRoundEnabled;

        const hasNoInProgressRounds = !rounds.find((roundItem: any) => roundItem.isActive);
        const isPensionModule = selectedModule.data.moduleName === Modules.pension;

        return isRoundNotBeingCreated && isCreateNewRoundEnabled && (hasNoInProgressRounds || isPensionModule);
    };

    const RoundColumn = (params: any) => {
        const currentRowRound = params.row.original;

        return (
            <span className="pointer" onClick={() => onSelectRound(currentRowRound)}>
                {currentRowRound.round}
            </span>
        );
    };

    const CreatedByColumn = (params: any) => {
        const currentRowRound = params.row.original;
        const creationReason = currentRowRound.creationReason;
        const createdBy = allUsers?.find((u: any) => u._id === currentRowRound.createdBy);

        let userName = '';
        if (createdBy) {
            if (typeof createdBy === 'object' && createdBy.fullName) {
                userName = createdBy.fullName;
            } else if (typeof createdBy === 'object' && (createdBy.firstName || createdBy.lastName)) {
                userName = `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim();
            } else {
                // If it's just an ID, we can try to find it in users context
                const user = allUsers?.find((u: any) => u._id === createdBy);
                if (user) {
                    userName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                }
            }
        }

        return (
            <div style={{ fontSize: '0.9rem' }}>
                {userName && (
                    <div style={{ marginBottom: creationReason ? '4px' : '0' }}>
                        <strong>{userName}</strong>
                    </div>
                )}
                {creationReason && <div style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>{creationReason}</div>}
                {!userName && !creationReason && <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>}
            </div>
        );
    };

    const ProgressColumn = (params: any) => {
        const currentRowRound = params.row.original;

        const getCss = () => {
            return {
                width: currentRowRound.progressPercentage + '%',
            };
        };

        // create a function that returns the css for the progress bar
        // the progress text should be centered
        // the progress value background should be #4d78cd
        const getChild = () => (
            <div className="progress-container">
                <div className="progress-color" style={getCss()} />
                <div className="progress-value">{currentRowRound.progressPercentage}%</div>
            </div>
        );

        return (
            <div className="progress-bar-wrapper">
                <ProgressBar
                    variant={'info'}
                    now={currentRowRound.progressPercentage}
                    // label={`${ currentRowRound.progressPercentage }%`}
                    // visuallyHidden={true}
                    children={getChild()}
                />
            </div>
        );
    };

    const onDeleteRound = (round: any) => async () => {
        const module = selectedModule?.data?.moduleName;

        try {
            if (!window.confirm(`Are you sure you want to delete ${camelCaseToText(module)} round ${round.round}?`)) return;

            let result;
            if (module === Modules.pension) {
                result = await deletePension(round._id);
            }
            if (module === Modules.insurance) {
                result = await deleteInsurance(round._id);
            } else {
                return;
            }

            const tmpRounds = [...rounds];
            const index = tmpRounds.findIndex((roundItem: any) => roundItem._id === round._id);
            tmpRounds.splice(index, 1);
            setRounds(tmpRounds);
            window.location.reload();
        } catch (err: any) {
            globalErrorHandler(err);
        }
    };

    const isDeleteShown = (round: any) => {
        const module = selectedModule?.data?.moduleName;
        if (module === Modules.pension && round.status === 'initial' && round.round !== 1) return true;
        if (module === Modules.insurance && round.status === 'idle') return true;
        return false;
    };

    const ActionColumn = ({ row }: { row: any }) => {
        const round = row.original;
        return (
            <>
                {isDeleteShown(round) && (
                    <span className="action-icon" onClick={onDeleteRound(round)}>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="">Delete {camelCaseToText(selectedModule?.data?.moduleName)}</Tooltip>}>
                            <i className="mdi mdi-delete"></i>
                        </OverlayTrigger>
                    </span>
                )}
            </>
        );
    };

    const StatusColumn = (params: any) => {
        const currentRowRound = params.row.original;
        const status = currentRowRound.status;
        const lastStatusChangeDate = currentRowRound.lastStatusChangeDate;

        const lastStatusChanger = allUsers?.find((u: any) => u._id === currentRowRound.lastStatusChanger);

        const getChangerString = () => {
            if (!lastStatusChanger) return '(changed automatically)';
            const { firstName, lastName } = lastStatusChanger;
            return `by ${firstName || ''} ${lastName || ''}`.trim();
        };

        return (
            <div style={{ fontSize: '0.9rem' }}>
                <div style={{ marginBottom: '4px' }}>
                    <strong>{camelCaseToText(status)}</strong>
                </div>
                {lastStatusChangeDate && (
                    <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {formatDate(new Date(lastStatusChangeDate))} {getChangerString()}
                    </div>
                )}
            </div>
        );
    };

    const columns = [
        {
            Header: 'Round',
            accessor: 'round',
            sort: true,
            Cell: RoundColumn,
        },
        {
            Header: 'Created By',
            accessor: 'createdBy',
            sort: false,
            Cell: CreatedByColumn,
        },
        {
            Header: 'Start Date',
            accessor: 'createdAt',
            sort: true,
            Cell: DateColumn,
        },
        {
            Header: 'End Date',
            accessor: 'endTime',
            sort: true,
            Cell: DateColumn,
        },
        {
            Header: 'Status',
            accessor: 'status',
            sort: false,
            Cell: StatusColumn,
        },
        {
            Header: 'Progress',
            accessor: 'progressPercentage',
            sort: false,
            Cell: ProgressColumn,
        },
        {
            Header: 'Actions',
            accessor: 'delete',
            sort: false,
            Cell: ActionColumn,
        },
    ];

    const getOverlayText = () => {
        return 'Creating new round is enabled only if there are no round in progress';
    };

    const handleCreateNewRound = () => {
        // if there is already a round running, ask a confirmation question before continuing
        if (selectedModule.data.moduleName === Modules.pension && rounds.find((roundItem: any) => roundItem.progressStatus === 'inProgress')) {
            if (!window.confirm(t('There is already a round in progress. Are you sure you want to create a new round?'))) {
                return;
            }
        }
        onCreateNewRound(client._id, currentModuleName, onCreateRound);
    };

    return (
        <div className="timeline-table card-shadow">
            <div className="time-line-header">
                <div className="title">{camelCaseToText(selectedModule?.data?.moduleName)} Timeline</div>
                {client && canCreateNewRound() ? (
                    <div>
                        {SecondButton}{' '}
                        <Button onClick={handleCreateNewRound} className="create-round btn-primary revert" disabled={isRoundBeingCreated}>
                            {isRoundBeingCreated ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                    Creating...
                                </>
                            ) : (
                                `Create ${camelCaseToText(selectedModule?.data?.moduleName)}`
                            )}
                        </Button>
                    </div>
                ) : (
                    <OverlayTrigger placement="bottom" overlay={<Tooltip id="">{getOverlayText()}</Tooltip>}>
                        <Button className={`create-round btn-primary revert ${canCreateNewRound() ? '' : 'blocked'}`} disabled={isRoundBeingCreated}>
                            {isRoundBeingCreated ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    Create {camelCaseToText(selectedModule?.data?.moduleName)} <i className={'fe-alert-circle'}></i>
                                </>
                            )}
                        </Button>
                    </OverlayTrigger>
                )}
            </div>

            {!columns || !rounds.length ? (
                <div className="no-data">This list is empty!</div>
            ) : (
                <>
                    <Table
                        columns={columns}
                        data={rounds}
                        onRowClicked={(row) => onSelectRound(row.original)}
                        cellClick={[0, 1, 2, 3]}
                        isSortable={true}
                        pagination={false}
                        isSelectable={false}
                        tableClass=""
                    />
                </>
            )}
        </div>
    );
};

export default TimeLine;
